import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { DateType } from 'api/models';
import { SprintPlanningAPI } from 'api/sprint-planning';
import {
  Sprint,
  SprintConfig,
  SprintsList,
  Epic,
  Backlog,
} from 'api/sprint-planning/types';
import { ViewApI } from 'api/views';
import type {
  AddTaskMutationType,
  ApiSprintListViewDefinition,
  CompleteSprintPayload,
  SprintGridPayload,
  SprintInfoPayload,
  SprintRemoveTaskPayload,
  EpicInfoPayload,
  ApiEpicListViewDefinition,
  EpicGridPayload,
  AddTasksToEpicMutationType,
  EpicRemoveTaskPayload,
  ApiBacklogListViewDefinition,
  ApiBacklogSprintList,
  AddTasksToSprintMutationType,
} from 'components/organisms/sprint-planning/types';
import { fiveMinutes } from './constants';

export const getSprintConfigurationKey = () => ['SPRINT_CONFIG'];

const useGetSprintConfiguration = () => {
  return useQuery(
    getSprintConfigurationKey(),
    () => SprintPlanningAPI.getSprintConfiguration(),
    {
      cacheTime: fiveMinutes,
      refetchOnWindowFocus: false,
    },
  );
};

const useUpdateConfigMutation = (): UseMutationResult<
  unknown,
  unknown,
  SprintConfig,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation((payload) => SprintPlanningAPI.updateConfig(payload), {
    onSuccess: async (config) => {
      queryClient.setQueryData(getSprintConfigurationKey(), config);
    },
  });
};

const useCreateSprintMutation = (
  options,
): UseMutationResult<Sprint, unknown, SprintInfoPayload, unknown> => {
  return useMutation((payload) => SprintPlanningAPI.createSprint(payload), {
    ...options,
  });
};

const useAddTasksMutation = (
  options,
): UseMutationResult<Sprint, unknown, AddTaskMutationType, unknown> => {
  return useMutation(
    ({ sprintId, payload }) =>
      SprintPlanningAPI.addTasksToSprint(sprintId, payload),
    {
      ...options,
    },
  );
};

export const sprintDetailQueryKey = (sprintId: number): [string, number] => [
  'SPRINT_DETAIL',
  sprintId,
];
const useGetSprintDetailQuery = (
  sprintId: number,
  options?: UseQueryOptions<Sprint, unknown, Sprint, [string, number]>,
) => {
  return useQuery(
    sprintDetailQueryKey(sprintId),
    () => SprintPlanningAPI.getSprint(sprintId),
    {
      enabled: !!sprintId,
      cacheTime: fiveMinutes,
      ...options,
    },
  );
};

const useGetSprintListViewsQuery = (options) => {
  return useQuery(
    ['sprint-list-views'],
    () => ViewApI.getSprintListViews<ApiSprintListViewDefinition>(),
    {
      ...options,
    },
  );
};

const useGetTaskCountInSprintQuery = ({
  isEnabled,
  sprintId,
  options,
}: {
  isEnabled: boolean;
  sprintId: number;
  options?: UseQueryOptions<number, unknown, number, [string, number]>;
}) => {
  return useQuery(
    ['tasks-count', sprintId],
    () => SprintPlanningAPI.getTaskCountInSprint(sprintId),
    {
      enabled: isEnabled,
      ...options,
    },
  );
};

const useUpdateSprint = (
  options?: UseMutationOptions<Sprint, unknown, SprintGridPayload, unknown>,
): UseMutationResult<Sprint, unknown, SprintGridPayload, unknown> => {
  return useMutation<Sprint, unknown, SprintGridPayload>({
    mutationFn: ({ sprintId, payload }: SprintGridPayload) =>
      SprintPlanningAPI.updateSprint(sprintId, payload),
    ...options,
  });
};

const useCompleteSprint = (
  options?: UseMutationOptions<
    Sprint,
    unknown,
    CompleteSprintPayload,
    string[]
  >,
): UseMutationResult<Sprint, unknown, CompleteSprintPayload, unknown> => {
  return useMutation({
    mutationFn: ({ sprintId, payload }: CompleteSprintPayload) => {
      return SprintPlanningAPI.completeSprint({ sprintId, payload });
    },

    ...options,
  });
};

const useRemoveTaskFromSprint = (): UseMutationResult<
  void,
  unknown,
  SprintRemoveTaskPayload,
  unknown
> => {
  return useMutation(({ sprintId, payload }: SprintRemoveTaskPayload) =>
    SprintPlanningAPI.removeTaskFromSprint({ sprintId, payload }),
  );
};
const useDeleteSprint = (): UseMutationResult<
  void,
  unknown,
  { sprintId: number },
  unknown
> => {
  return useMutation(({ sprintId }: { sprintId: number }) =>
    SprintPlanningAPI.deleteSprint({ sprintId }),
  );
};

export const sprintsForTimeRange = 'SPRINTS_FOR_TIME_RANGE';

const useGetSprintsInTimeRange = ({
  startDate,
  endDate,
  projectIds,
  inComplete,
  enabled,
  isAlreadyAddedToPhase = false,
  onSuccess,
  sprintId,
}: {
  startDate: DateType;
  endDate: DateType;
  projectIds?: number;
  inComplete?: boolean;
  enabled: boolean;
  sprintId?: number;
  isAlreadyAddedToPhase?: boolean;
  onSuccess?: (data: SprintsList) => void;
}) => {
  return useQuery(
    [sprintsForTimeRange, startDate, endDate],
    () =>
      SprintPlanningAPI.getSprintsList({
        startDate,
        endDate,
        projectIds,
        inComplete,
        isAlreadyAddedToPhase,
        sprintId,
      }),

    { refetchOnMount: true, enabled, onSuccess },
  );
};

export const tasksInSprintForExternalUser = 'TASKS_IN_SPRINT_FOR_EXTERNAL_USER';

const useGetTasksInSprintForExternalUser = ({ projectId, sprintId }) => {
  return useQuery(
    [tasksInSprintForExternalUser, sprintId],
    () =>
      SprintPlanningAPI.getTasksInSprintForExternalUser({
        projectId,
        sprintId,
      }),
    { refetchOnMount: true },
  );
};

const useSprintsCount = () => {
  return useQuery(['sprints-count'], () => SprintPlanningAPI.sprintsCount(), {
    refetchOnMount: true,
  });
};

// Epic queries
export const epicDetailQueryKey = (epicId: number): [string, number] => [
  'EPIC_DETAIL',
  epicId,
];

const useGetEpicDetailQuery = (
  epicId: number,
  options?: UseQueryOptions<Epic, unknown, Epic, [string, number]>,
) => {
  return useQuery(
    epicDetailQueryKey(epicId),
    () => SprintPlanningAPI.getEpic(epicId),
    {
      enabled: !!epicId,
      cacheTime: fiveMinutes,
      ...options,
    },
  );
};

const useCreateEpicMutation = (
  options,
): UseMutationResult<Epic, unknown, EpicInfoPayload, unknown> => {
  return useMutation(
    (payload) => SprintPlanningAPI.createEpic(payload as Epic),
    {
      ...options,
    },
  );
};

const useUpdateEpicMutation = (
  options?: UseMutationOptions<Epic, unknown, EpicGridPayload, unknown>,
): UseMutationResult<Epic, unknown, EpicGridPayload, unknown> => {
  return useMutation<Epic, unknown, EpicGridPayload>({
    mutationFn: ({ epicId, payload }: EpicGridPayload) =>
      SprintPlanningAPI.updateEpic(epicId, payload),
    ...options,
  });
};

const useDeleteEpicMutation = (): UseMutationResult<
  void,
  unknown,
  { epicId: number },
  unknown
> => {
  return useMutation(({ epicId }) => SprintPlanningAPI.deleteEpic(epicId));
};

const useArchiveEpicMutation = (): UseMutationResult<
  void,
  unknown,
  { epicId: number },
  unknown
> => {
  return useMutation(({ epicId }) => SprintPlanningAPI.archiveEpic(epicId));
};

const useUnarchiveEpicMutation = (): UseMutationResult<
  void,
  unknown,
  { epicId: number },
  unknown
> => {
  return useMutation(({ epicId }) => SprintPlanningAPI.unarchiveEpic(epicId));
};

const useAddTasksToEpicMutation = (
  options,
): UseMutationResult<Epic, unknown, AddTasksToEpicMutationType, unknown> => {
  return useMutation(
    ({ epicId, payload }) => SprintPlanningAPI.addTasksToEpic(epicId, payload),
    {
      ...options,
    },
  );
};

const useRemoveTaskFromEpicMutation = (): UseMutationResult<
  void,
  unknown,
  EpicRemoveTaskPayload,
  unknown
> => {
  return useMutation(({ epicId, payload }) =>
    SprintPlanningAPI.removeTaskFromEpic({ epicId, payload }),
  );
};

const useEpicsCount = () => {
  return useQuery(['epics-count'], () => SprintPlanningAPI.getEpicsCount(), {
    refetchOnMount: true,
  });
};

const useGetEpicsForAccount = () => {
  return useQuery(
    ['epics-for-account'],
    () => SprintPlanningAPI.getEpicsForAccount(),
    {
      refetchOnMount: true,
    },
  );
};

const useGetEpicListViewsQuery = (options) => {
  return useQuery(
    ['epic-list-views'],
    () => ViewApI.getEpicListViews<ApiEpicListViewDefinition>(),
    {
      ...options,
    },
  );
};

// Backlog queries

const useBacklogsCount = () => {
  return useQuery(
    ['backlogs-count'],
    () => SprintPlanningAPI.getBacklogsCount(),
    {
      refetchOnMount: true,
    },
  );
};

const useCreateBacklogMutation = (
  options,
): UseMutationResult<Backlog, unknown, Backlog, unknown> => {
  return useMutation((payload) => SprintPlanningAPI.createBacklog(payload), {
    ...options,
  });
};
const useUpdateBacklogMutation = (
  options,
): UseMutationResult<
  Backlog,
  unknown,
  { backlogId: number; payload: Backlog },
  unknown
> => {
  return useMutation(
    ({ backlogId, payload }) =>
      SprintPlanningAPI.updateBacklog(backlogId, payload),
    {
      ...options,
    },
  );
};

export const backlogDetailQueryKey = (backlogId: number): [string, number] => [
  'BACKLOG_DETAIL',
  backlogId,
];

export const backlogSprintsListQueryKey = (
  backlogId: number,
): [string, number] => ['BACKLOG_SPRINT_LIST', backlogId];

const useGetBacklogDetailQuery = (
  backlogId: number,
  options?: UseQueryOptions<Backlog, unknown, Backlog, [string, number]>,
) => {
  return useQuery(
    backlogDetailQueryKey(backlogId),
    () => SprintPlanningAPI.getBacklog(backlogId),
    {
      enabled: !!backlogId,
      ...options,
    },
  );
};
const useGetBacklogSprintsQuery = (
  backlogId: number,
  options?: UseQueryOptions<Sprint[], unknown, Sprint[], [string, number]>,
) => {
  return useQuery(
    backlogSprintsListQueryKey(backlogId),
    () => SprintPlanningAPI.getBacklogSprints(backlogId),
    {
      enabled: !!backlogId,
      ...options,
    },
  );
};

const useArchiveBacklogMutation = (): UseMutationResult<
  void,
  unknown,
  { backlogId: number },
  unknown
> => {
  return useMutation(({ backlogId }) =>
    SprintPlanningAPI.archiveBacklog(backlogId),
  );
};

const useUnarchiveBacklogMutation = (): UseMutationResult<
  void,
  unknown,
  { backlogId: number },
  unknown
> => {
  return useMutation(({ backlogId }) =>
    SprintPlanningAPI.unarchiveBacklog(backlogId),
  );
};

const useDeleteBacklogMutation = (): UseMutationResult<
  void,
  unknown,
  { backlogId: number },
  unknown
> => {
  return useMutation(({ backlogId }: { backlogId: number }) =>
    SprintPlanningAPI.deleteBacklog({ backlogId }),
  );
};

const useGetBacklogListViewsQuery = (options) => {
  return useQuery(
    ['backlog-list-views'],
    () => ViewApI.getBacklogListViews<ApiBacklogListViewDefinition>(),
    {
      ...options,
    },
  );
};

export const backlogSprintListQueryKey = (
  backlogId: number,
): [string, number] => ['BACKLOG_SPRINT_LIST', backlogId];

const useAddTasksToSprintFromBacklogMutation = (
  options,
): UseMutationResult<
  Sprint,
  unknown,
  AddTasksToSprintMutationType,
  unknown
> => {
  return useMutation(
    ({ backlogId, sprintId, payload }) =>
      SprintPlanningAPI.addTasksToSprintFromBacklog(
        backlogId,
        sprintId,
        payload,
      ),
    {
      ...options,
    },
  );
};

const useGetBacklogSprintListQuery = (
  backlogId: number,
  options?: UseQueryOptions<
    ApiBacklogSprintList[],
    unknown,
    ApiBacklogSprintList[],
    [string, number]
  >,
) => {
  return useQuery(
    backlogSprintListQueryKey(backlogId),
    () => SprintPlanningAPI.getBacklogSprintList(backlogId),
    {
      ...options,
    },
  );
};

export {
  useGetSprintConfiguration,
  useUpdateConfigMutation,
  useCreateSprintMutation,
  useGetSprintDetailQuery,
  useUpdateSprint,
  useAddTasksMutation,
  useRemoveTaskFromSprint,
  useGetSprintsInTimeRange,
  useGetTasksInSprintForExternalUser,
  useDeleteSprint,
  useCompleteSprint,
  useGetTaskCountInSprintQuery,
  useGetSprintListViewsQuery,
  useSprintsCount,
  useGetEpicDetailQuery,
  useCreateEpicMutation,
  useUpdateEpicMutation,
  useDeleteEpicMutation,
  useAddTasksToEpicMutation,
  useRemoveTaskFromEpicMutation,
  useEpicsCount,
  useGetEpicListViewsQuery,
  useArchiveEpicMutation,
  useUnarchiveEpicMutation,
  useGetEpicsForAccount,
  useBacklogsCount,
  useCreateBacklogMutation,
  useGetBacklogDetailQuery,
  useUpdateBacklogMutation,
  useArchiveBacklogMutation,
  useUnarchiveBacklogMutation,
  useGetBacklogListViewsQuery,
  useDeleteBacklogMutation,
  useAddTasksToSprintFromBacklogMutation,
  useGetBacklogSprintListQuery,
  useGetBacklogSprintsQuery,
};
